import { clamp } from '@/lib/motion'

export type GlassMap = {
    url: string
    size: number
    scale: number
}

const N_AIR = 1
const N_GLASS = 1.5

/**
 * Bakes a convex-lens displacement map for one disc box.
 *
 * The disc is modelled as a glass slab: flat across the middle, curving down to
 * the backdrop over a bezel band at the rim following the convex squircle
 * `y = (1 - (1 - t)^4)^(1/4)`. A straight-down ray is refracted through the
 * surface with Snell's law (air n=1 -> glass n=1.5) and the lateral landing
 * offset is recorded per pixel: ~zero across the flat interior, peaking just
 * inside the rim. R holds the x-offset, G the y-offset, normalised around 128.
 */
export const bakeGlassMap = (
    box: number,
    bezelRatio: number,
    thickness: number
): GlassMap | null => {
    if (typeof document === 'undefined') return null

    const size = Math.max(32, Math.round(box))
    const canvas = document.createElement('canvas')

    canvas.width = size
    canvas.height = size

    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    const radius = size / 2
    const bezel = Math.max(1, size * bezelRatio)
    const eta = N_AIR / N_GLASS

    const offX = new Float32Array(size * size)
    const offY = new Float32Array(size * size)

    let peak = 0.0001

    for (let py = 0; py < size; py++) {
        for (let px = 0; px < size; px++) {
            const x = px + 0.5 - radius
            const y = py + 0.5 - radius
            const r = Math.sqrt(x * x + y * y)

            if (r < 0.0001 || r > radius) continue

            // t: 0 at the rim, 1 at the inner edge of the bezel band
            const t = (radius - r) / bezel
            if (t >= 1) continue

            const tc = clamp(t, 0.0001, 1)
            const k = 1 - tc
            const a = 1 - k * k * k * k
            if (a <= 0.000001) continue

            // d/dt of a^(1/4), with a = 1 - (1 - t)^4
            const dHdt = k * k * k * Math.pow(a, -0.75)

            // surface gradient along the radius (negative: it falls to the rim)
            const slope = -(dHdt * thickness) / bezel
            const len = Math.sqrt(slope * slope + 1)

            // outward-tilted surface normal in the (radial, z) plane
            const nr = -slope / len
            const nz = 1 / len

            const cosI = nz
            const sin2T = eta * eta * (1 - cosI * cosI)
            if (sin2T >= 1) continue

            const cosT = Math.sqrt(1 - sin2T)
            const f = eta * cosI - cosT

            // refracted direction, incident ray straight down = (0, -1)
            const tr = f * nr
            const tz = -eta + f * nz
            if (tz >= 0) continue

            const lat = clamp((thickness * tr) / -tz, -bezel, bezel)
            const idx = py * size + px
            const vx = (x / r) * lat
            const vy = (y / r) * lat

            offX[idx] = vx
            offY[idx] = vy

            const m = Math.max(Math.abs(vx), Math.abs(vy))
            if (m > peak) peak = m
        }
    }

    const image = ctx.createImageData(size, size)
    const data = image.data

    for (let i = 0; i < size * size; i++) {
        const p = i * 4

        data[p] = Math.round(clamp(128 + (offX[i] / peak) * 127, 0, 255))
        data[p + 1] = Math.round(clamp(128 + (offY[i] / peak) * 127, 0, 255))
        data[p + 2] = 128
        data[p + 3] = 255
    }

    ctx.putImageData(image, 0, 0)

    return {
        url: canvas.toDataURL(),
        size,
        // feDisplacementMap shifts by scale * (C - 0.5); C spans 0.5 ± 127/255
        scale: (peak * 255) / 127
    }
}

/** `backdrop-filter: url(...)` is Chromium-only and lies to @supports elsewhere */
export const supportsNativeGlass = (): boolean => {
    if (typeof navigator === 'undefined') return false

    const ua = navigator.userAgent

    return /Chrome\/|Chromium\/|Edg\//.test(ua) && !/OPiOS|FxiOS|CriOS/.test(ua)
}
